/**
 * The tour engine (spec 25 section 5): a state machine and nothing else.
 *
 * No DOM, no router, no storage, no library. It knows which step is current,
 * what "next" and "back" mean, and whether the tour is running, paused or
 * over. TourHost.vue is the only thing that drives it, and everything it
 * needs from the world (the user's role, where to start) is handed in. That
 * is what makes it testable without a browser, and what keeps a tour bug from
 * being a page bug.
 *
 * A definition is chapters of steps (definitions/*.js). The engine flattens
 * them, drops steps the user's role excludes (and with them any chapter left
 * empty), and keeps each step's chapter beside it so the card can say
 * "Track it live, step 5 of 11".
 *
 * Spec 33 adds what a walkthrough that DOES things needs, without changing
 * what the story tour already does:
 *  - `goTo(stepId)` for branch points, with a history so Back retraces the
 *    branch the visitor actually took rather than the definition's order;
 *  - `skip()`, a Next that is recorded as a skip (analytics tells them apart);
 *  - a FLOOR: `next({ commit: true })` leaves a step whose action wrote to the
 *    server, and Back can no longer cross it. Backing up past "Create load"
 *    would show a form the load has already been made from, and the only
 *    honest way to "undo" a real write is another real write the visitor
 *    asked for. Steps before a commit are re-playable; steps after are not.
 *  - `lastMove` on every snapshot ('start' | 'next' | 'skip' | 'back' |
 *    'jump' | 'resume' | 'pause' | 'end'), so the host can say what happened.
 */

export const STATUS = Object.freeze({
  IDLE: 'idle',
  RUNNING: 'running',
  PAUSED: 'paused',
  ENDED: 'ended',
});

function allowedFor(step, role) {
  if (!Array.isArray(step.roles) || step.roles.length === 0) return true;
  return Boolean(role) && step.roles.includes(role);
}

/**
 * Whether a step's context conditions hold (spec 33 section 5.1): every key
 * in `requires` is known, and none in `unless` is. With no context given,
 * nothing is filtered, which is what the contract test and the story want.
 */
export function contextAllows(step, context) {
  if (!context) return true;
  const known = key => context[key] !== undefined && context[key] !== null && context[key] !== '';
  const requires = Array.isArray(step.requires) ? step.requires : [];
  const unless = Array.isArray(step.unless) ? step.unless : [];
  return requires.every(known) && !unless.some(known);
}

/** Every step the user may see, in order, each carrying its chapter. */
export function flattenSteps(definition, role = null, context = null) {
  const out = [];
  for (const chapter of (definition && definition.chapters) || []) {
    for (const step of chapter.steps || []) {
      if (!allowedFor(step, role)) continue;
      if (!contextAllows(step, context)) continue;
      out.push({ ...step, chapterId: chapter.id, chapterTitle: chapter.title });
    }
  }
  return out;
}

/**
 * @param {object} options
 * @param {object} options.definition a tour definition
 * @param {string|null} [options.role] the signed-in user's role
 * @param {object|null} [options.context] what the walkthrough knows at start
 *   (spec 33 5.5); steps whose `requires` / `unless` it fails are left out
 * @param {(state: object) => void} [options.onChange] called after every transition
 */
export function createEngine({ definition, role = null, context = null, onChange = () => {} }) {
  const steps = flattenSteps(definition, role, context);
  let status = STATUS.IDLE;
  let index = 0;
  let endReason = null;
  let floor = 0;
  let history = [];
  let lastMove = null;

  const snapshot = () => ({
    tourId: definition.id,
    status,
    index,
    total: steps.length,
    step: status === STATUS.RUNNING || status === STATUS.PAUSED ? steps[index] || null : null,
    isFirst: index === 0,
    isLast: index === steps.length - 1,
    canBack: index > floor && (history.length > 0 || index > 0),
    floor,
    history: history.slice(),
    lastMove,
    endReason,
  });

  const changed = move => {
    lastMove = move;
    onChange(snapshot());
    return snapshot();
  };

  return {
    steps,
    state: snapshot,

    /** The position of a step by its id, or -1. */
    indexOf(stepId) {
      return steps.findIndex(s => s.id === stepId);
    },

    /**
     * Begin at a chapter (an email's deep link), at a step index (a restored
     * tour), or at the top. A restored tour also brings back its floor and
     * the branch history, so Back still respects both after a refresh.
     */
    start({ chapter = null, at = null, floor: savedFloor = 0, history: savedHistory = [] } = {}) {
      if (steps.length === 0) return this.end('empty');
      const fromChapter = chapter ? steps.findIndex(s => s.chapterId === chapter) : -1;
      const fromIndex = Number.isInteger(at) && at >= 0 && at < steps.length ? at : -1;
      index = fromIndex >= 0 ? fromIndex : fromChapter >= 0 ? fromChapter : 0;
      const valid = i => Number.isInteger(i) && i >= 0 && i < index;
      floor = fromIndex >= 0 && valid(savedFloor) ? savedFloor : 0;
      history =
        fromIndex >= 0 && Array.isArray(savedHistory) ? savedHistory.filter(valid).slice(-50) : [];
      status = STATUS.RUNNING;
      endReason = null;
      return changed('start');
    },

    /** @param {{ commit?: boolean }} [options] commit: the step just left wrote to the server */
    next({ commit = false } = {}) {
      if (status !== STATUS.RUNNING) return snapshot();
      if (index >= steps.length - 1) return this.end('completed');
      history.push(index);
      index += 1;
      if (commit) floor = index;
      return changed('next');
    },

    /** Move on without doing the step. Never a commit: nothing was written. */
    skip() {
      if (status !== STATUS.RUNNING) return snapshot();
      if (index >= steps.length - 1) return this.end('completed');
      history.push(index);
      index += 1;
      return changed('skip');
    },

    /** A branch point's choice: jump to a step by id. Unknown ids change nothing. */
    goTo(stepId, { commit = false } = {}) {
      if (status !== STATUS.RUNNING) return snapshot();
      const to = steps.findIndex(s => s.id === stepId);
      if (to === -1 || to === index) return snapshot();
      history.push(index);
      index = to;
      if (commit) floor = index;
      return changed('jump');
    },

    back() {
      if (status !== STATUS.RUNNING || index === 0 || index <= floor) return snapshot();
      let to = history.length ? history.pop() : index - 1;
      // A history entry under the floor is a step whose write already happened.
      if (to < floor) to = floor;
      if (to === index) return snapshot();
      index = to;
      return changed('back');
    },

    /** The visitor wandered off the path. The pill brings them back to the same step. */
    pause() {
      if (status !== STATUS.RUNNING) return snapshot();
      status = STATUS.PAUSED;
      return changed('pause');
    },

    resume() {
      if (status !== STATUS.PAUSED) return snapshot();
      status = STATUS.RUNNING;
      return changed('resume');
    },

    /** @param {'completed'|'exited'|'empty'|'interrupted'} reason */
    end(reason = 'exited') {
      if (status === STATUS.ENDED) return snapshot();
      status = STATUS.ENDED;
      endReason = reason;
      return changed('end');
    },
  };
}
